import axios from 'axios';
import authService from './authService';

// Usar o mesmo webhook da autenticação
const WEBHOOK_URL = authService.WEBHOOK_URL;

/**
 * Serviço para gerenciar produtos e seus dados
 */
const productService = {
  /**
   * Salva um novo produto no sistema
   * @param {Object} productData - Dados do produto a ser salvo
   * @returns {Promise<Object>} - Resposta da API
   */
  saveProduct: async (productData) => {
    try {
      // Obter dados do usuário para associar ao produto
      const userData = authService.getUserData();
      
      if (!userData || !userData.telefone) {
        throw new Error('Usuário não autenticado');
      }
      
      // Enviar dados do produto para o webhook
      const response = await axios.post(WEBHOOK_URL, {
        ...productData,
        telefone: userData.telefone,
        acao: 'salvar_produto'
      });
      
      // Se o salvamento foi bem-sucedido, atualizar os dados do usuário
      if (response.data === 'sucesso') {
        // Obter dados atuais do usuário
        const currentUserData = authService.getUserData();
        
        // Adicionar o produto à lista de produtos do usuário
        const updatedUserData = {
          ...currentUserData,
          produtos: currentUserData.produtos 
            ? [...currentUserData.produtos, productData]
            : [productData]
        };
        
        // Atualizar dados no localStorage
        authService.setUserData(updatedUserData);
        
        // Simular resposta de sucesso para desenvolvimento
        return 'sucesso';
      }
      
      return response.data;
    } catch (error) {
      console.error('Erro ao salvar produto:', error);
      
      // Para desenvolvimento, criar um produto de exemplo no localStorage
      // mesmo se a API falhar
      try {
        const currentUserData = authService.getUserData();
        
        if (currentUserData) {
          const updatedUserData = {
            ...currentUserData,
            produtos: currentUserData.produtos 
              ? [...currentUserData.produtos, productData]
              : [productData]
          };
          
          authService.setUserData(updatedUserData);
          console.log('Produto salvo localmente para desenvolvimento');
          return 'sucesso';
        }
      } catch (localError) {
        console.error('Erro ao salvar produto localmente:', localError);
      }
      
      throw error;
    }
  },
  
  /**
   * Obtém todos os produtos do usuário atual
   * @returns {Array} - Lista de produtos do usuário
   */
  getUserProducts: () => {
    try {
      const userData = authService.getUserData();
      
      if (!userData) {
        return [];
      }
      
      return userData.produtos || [];
    } catch (error) {
      console.error('Erro ao obter produtos do usuário:', error);
      return [];
    }
  },
  
  /**
   * Obtém um produto específico pelo ID
   * @param {string} productId - ID do produto
   * @returns {Object|null} - Dados do produto ou null se não encontrado
   */
  getProductById: (productId) => {
    try {
      const products = productService.getUserProducts();
      return products.find(product => product.id === productId) || null;
    } catch (error) {
      console.error('Erro ao obter produto por ID:', error);
      return null;
    }
  },
  
  /**
   * Atualiza um produto existente
   * @param {string} productId - ID do produto a ser atualizado
   * @param {Object} updatedData - Novos dados do produto
   * @returns {Promise<Object>} - Resposta da API
   */
  updateProduct: async (productId, updatedData) => {
    try {
      const userData = authService.getUserData();
      
      if (!userData || !userData.telefone) {
        throw new Error('Usuário não autenticado');
      }
      
      // Enviar dados atualizados para o webhook
      const response = await axios.post(WEBHOOK_URL, {
        ...updatedData,
        id: productId,
        telefone: userData.telefone,
        acao: 'atualizar_produto'
      });
      
      // Se a atualização foi bem-sucedida, atualizar os dados locais
      if (response.data === 'sucesso') {
        const currentUserData = authService.getUserData();
        const currentProducts = currentUserData.produtos || [];
        
        // Substituir o produto antigo pelo atualizado
        const updatedProducts = currentProducts.map(product => 
          product.id === productId ? { ...product, ...updatedData } : product
        );
        
        // Atualizar dados no localStorage
        authService.setUserData({
          ...currentUserData,
          produtos: updatedProducts
        });
      }
      
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar produto:', error);
      throw error;
    }
  },
  
  /**
   * Remove um produto
   * @param {string} productId - ID do produto a ser removido
   * @returns {Promise<Object>} - Resposta da API
   */
  deleteProduct: async (productId) => {
    try {
      const userData = authService.getUserData();
      
      if (!userData || !userData.telefone) {
        throw new Error('Usuário não autenticado');
      }
      
      // Enviar solicitação de remoção para o webhook
      const response = await axios.post(WEBHOOK_URL, {
        id: productId,
        telefone: userData.telefone,
        acao: 'remover_produto'
      });
      
      // Se a remoção foi bem-sucedida, atualizar os dados locais
      if (response.data === 'sucesso') {
        const currentUserData = authService.getUserData();
        const currentProducts = currentUserData.produtos || [];
        
        // Filtrar o produto removido
        const updatedProducts = currentProducts.filter(product => product.id !== productId);
        
        // Atualizar dados no localStorage
        authService.setUserData({
          ...currentUserData,
          produtos: updatedProducts
        });
      }
      
      return response.data;
    } catch (error) {
      console.error('Erro ao remover produto:', error);
      throw error;
    }
  },
  
  /**
   * Gera perguntas frequentes automaticamente com base nos dados do produto usando a API da OpenAI
   * @param {Object} productData - Dados do produto
   * @returns {Array} - Lista de perguntas frequentes geradas
   */
  generateFAQs: async (productData) => {
    try {
      // API Key da OpenAI
      const OPENAI_API_KEY = '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';
      
      // Obter o segmento de negócio do usuário
      const userData = authService.getUserData();
      const businessSegment = userData?.segmento || 'serviços';
      
      // Construir o prompt para a API da OpenAI
      const prompt = `Você é um especialista em atendimento ao cliente e marketing digital, com profundo conhecimento em diversos segmentos de mercado, especialmente em ${businessSegment}.

Gere 15 perguntas frequentes que clientes fazem por telefone ou mensagem sobre o produto/serviço "${productData.productName}" no segmento de "${businessSegment}".

As perguntas devem:
1. Focar PRINCIPALMENTE em dúvidas específicas do segmento "${businessSegment}" (este é o critério mais importante)
2. Incluir perguntas sobre preço, localização, formas de pagamento
3. Abordar dúvidas práticas que clientes normalmente perguntam antes de agendar
4. Ser formuladas como perguntas diretas que um cliente real faria por telefone ou mensagem
5. Ter linguagem natural e conversacional

Não inclua perguntas sobre agendamento, disponibilidade, cancelamentos e reagendamentos pois temos outro agente específico para isso.
Não inclua respostas, apenas as perguntas.
Retorne apenas as perguntas, uma por linha, sem numeração ou formatação adicional.`;
      
      // Configurar a requisição para a API da OpenAI
      const response = await axios.post(
        'https://api.openai.com/v1/chat/completions',
        {
          model: 'gpt-4o',
          messages: [
            {
              role: 'system',
              content: 'Você é um assistente especializado em criar perguntas frequentes para produtos e serviços.'
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: 0.7,
          max_tokens: 1000
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${OPENAI_API_KEY}`
          }
        }
      );
      
      // Processar a resposta da API
      const generatedQuestions = response.data.choices[0].message.content.split('\n').filter(line => line.trim() !== '');
      
      // Criar o array de FAQs com as perguntas geradas e respostas vazias
      const faqs = generatedQuestions.map((question, index) => ({
        id: index + 1,
        question: question.trim(),
        answer: ''
      }));
      
      return faqs;
    } catch (error) {
      console.error('Erro ao gerar FAQs com a API da OpenAI:', error);
      throw error;
    }
  }
};

export default productService;
